import React, { useMemo, useRef, useState, useCallback, useEffect } from "react";
import { jsPDF } from "jspdf";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import heic2any from "heic2any";
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf";
import {
  FiImage, FiFile, FiDownload, FiX, FiArrowUp, FiArrowDown,
  FiCheckCircle, FiAlertCircle, FiFilePlus, FiTrash2,
  FiFileText, FiZap, FiScissors,
} from "react-icons/fi";

import styles from "../styles/ImageToolsPage.module.css";

pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";

const BG_CDN = "https://cdn.jsdelivr.net/npm/@imgly/background-removal@1.7.0/dist/";

/* ── constants ── */
const ACCEPTED_MIMES = [
  "image/png","image/jpeg","image/jpg","image/webp",
  "image/avif","image/gif","image/bmp","image/x-ms-bmp",
  "image/tiff","image/x-tiff","image/svg+xml",
  "image/heic","image/heif",
  "image/x-icon","image/vnd.microsoft.icon",
  "application/pdf",
];

/* ── helpers ── */
function clamp(n, a, b) {
  const x = Number(n);
  if (!isFinite(x)) return a;
  return Math.min(b, Math.max(a, x));
}

async function readFileAsDataURL(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload  = () => resolve(String(r.result || ""));
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

async function loadImageFromDataURL(dataUrl) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload  = () => resolve(img);
    img.onerror = reject;
    img.src = dataUrl;
  });
}

function extFromMime(mime) {
  const m = String(mime || "").toLowerCase();
  if (m === "image/png")   return "png";
  if (m === "image/jpeg")  return "jpg";
  if (m === "image/webp")  return "webp";
  if (m === "image/bmp"  || m === "image/x-ms-bmp") return "bmp";
  if (m === "image/gif")   return "gif";
  if (m === "image/tiff" || m === "image/x-tiff")   return "tiff";
  if (m === "image/avif")  return "avif";
  if (m === "image/svg+xml") return "svg";
  if (m === "image/heic")  return "heic";
  if (m === "image/heif")  return "heif";
  if (m === "image/x-icon" || m === "image/vnd.microsoft.icon") return "ico";
  if (m === "application/pdf") return "pdf";
  return "img";
}

function makeSafeName(name) {
  return String(name || "image")
    .replace(/\.[^/.]+$/, "")
    .replace(/[^\w.-]+/g, "_")
    .slice(0, 80);
}

function isHeicLike(file) {
  const t = (file?.type || "").toLowerCase();
  return t === "image/heic" || t === "image/heif" ||
    /\.heic$/i.test(file?.name || "") || /\.heif$/i.test(file?.name || "");
}

function isPdfLike(file) {
  const t = (file?.type || "").toLowerCase();
  return t === "application/pdf" || /\.pdf$/i.test(file?.name || "");
}

async function normalizeInputFile(file) {
  if (isHeicLike(file)) {
    const blob = await heic2any({ blob: file, toType: "image/png", quality: 1 });
    const out  = Array.isArray(blob) ? blob[0] : blob;
    return new File([out], makeSafeName(file.name) + ".png", { type: "image/png" });
  }
  return file;
}

async function convertPdfToImages(file, scale = 2) {
  const pdf   = await pdfjsLib.getDocument({ data: await file.arrayBuffer() }).promise;
  const pages = [];

  for (let i = 1; i <= pdf.numPages; i++) {
    const page     = await pdf.getPage(i);
    const viewport = page.getViewport({ scale });
    const canvas   = document.createElement("canvas");
    const ctx      = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas not supported");
    canvas.width  = Math.ceil(viewport.width);
    canvas.height = Math.ceil(viewport.height);
    await page.render({ canvasContext: ctx, viewport }).promise;
    const blob = await new Promise(res => canvas.toBlob(res, "image/png", 1));
    if (!blob) throw new Error(`Failed page ${i}`);
    const outFile = new File([blob], `${makeSafeName(file.name)}_page_${i}.png`, { type: "image/png" });
    pages.push({
      file: outFile, name: outFile.name, type: outFile.type, size: outFile.size,
      preview: await readFileAsDataURL(outFile),
      unreadable: false, err: "",
      originalName: file.name, originalType: file.type || "application/pdf",
      fromPdf: true, pdfPageNumber: i,
    });
  }
  return pages;
}

async function convertImageFile(file, opts) {
  const { outMime, quality, maxW, maxH, keepAspect, jpgBg } = opts;
  const img  = await loadImageFromDataURL(await readFileAsDataURL(file));
  const srcW = img.naturalWidth  || img.width;
  const srcH = img.naturalHeight || img.height;
  let tW = srcW, tH = srcH;

  const mw = maxW ? clamp(maxW, 1, 99999) : null;
  const mh = maxH ? clamp(maxH, 1, 99999) : null;

  if (mw || mh) {
    if (keepAspect) {
      const r = Math.min(mw ? mw / srcW : 1, mh ? mh / srcH : 1);
      tW = Math.max(1, Math.round(srcW * r));
      tH = Math.max(1, Math.round(srcH * r));
    } else {
      tW = mw || srcW;
      tH = mh || srcH;
    }
  }

  const canvas = document.createElement("canvas");
  canvas.width  = tW;
  canvas.height = tH;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas not supported");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  if (outMime === "image/jpeg") { ctx.fillStyle = jpgBg || "#ffffff"; ctx.fillRect(0, 0, tW, tH); }
  ctx.drawImage(img, 0, 0, tW, tH);

  const q    = outMime === "image/png" ? 1 : clamp(quality, 0.1, 1);
  const blob = await new Promise(res => canvas.toBlob(res, outMime, q));
  if (!blob) throw new Error("Blob export failed");
  return { blob, width: tW, height: tH, outMime };
}

/* ══════════════════════════════════════════
   MAIN PAGE
══════════════════════════════════════════ */
export default function ImageToolsPage() {
  const [tab, setTab] = useState("convert");

  /* files */
  const [files, setFiles] = useState([]);
  const idRef = useRef(1);

  /* converter */
  const [outMime,     setOutMime]     = useState("image/png");
  const [quality,     setQuality]     = useState(0.92);
  const [maxW,        setMaxW]        = useState("");
  const [maxH,        setMaxH]        = useState("");
  const [keepAspect,  setKeepAspect]  = useState(true);
  const [outputMode,  setOutputMode]  = useState("zip");
  const [singleName,  setSingleName]  = useState("converted");
  const [jpgBg,       setJpgBg]       = useState("#ffffff");
  const [pdfRenderScale, setPdfRenderScale] = useState(2);

  /* pdf maker */
  const [pdfPage,        setPdfPage]        = useState("a4");
  const [pdfOrientation, setPdfOrientation] = useState("p");
  const [pdfMargin,      setPdfMargin]      = useState(10);
  const [pdfFit,         setPdfFit]         = useState("contain");

  /* background removal */
  const [bgFile,       setBgFile]       = useState(null);
  const [bgPreview,    setBgPreview]    = useState("");
  const [bgResultBlob, setBgResultBlob] = useState(null);
  const [bgResultUrl,  setBgResultUrl]  = useState("");
  const [bgBusy,       setBgBusy]       = useState(false);
  const [bgPct,        setBgPct]        = useState(0);
  const [bgPhase,      setBgPhase]      = useState("");
  const [bgModel,      setBgModel]      = useState("small");
  const [bgOutputMode, setBgOutputMode] = useState("transparent");
  const [bgFill,       setBgFill]       = useState("#ffffff");
  const [bgView,       setBgView]       = useState("before");

  /* ui */
  const [busy,    setBusy]    = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [msg,     setMsg]     = useState({ text: "", kind: "" });

  /* auto-dismiss alert */
  useEffect(() => {
    if (!msg.text) return;
    const t = setTimeout(() => setMsg({ text: "", kind: "" }), 5000);
    return () => clearTimeout(t);
  }, [msg.text]);

  const ok  = text => setMsg({ text, kind: "ok"  });
  const err = text => setMsg({ text, kind: "err" });
  const info = text => setMsg({ text, kind: "info" });

  const totalSizeMb = useMemo(
    () => (files.reduce((s, f) => s + (f.size || 0), 0) / 1048576).toFixed(2),
    [files]
  );

  /* ── file adding ── */
  const addFiles = useCallback(async (picked) => {
    setMsg({ text: "", kind: "" });
    const arr = Array.from(picked || []);
    if (!arr.length) return;

    const next     = [];
    const rejected = [];

    for (const raw of arr) {
      const t = (raw.type || "").toLowerCase();
      const supported =
        isPdfLike(raw) || isHeicLike(raw) ||
        ACCEPTED_MIMES.includes(t) || t.startsWith("image/") ||
        /\.(png|jpe?g|webp|avif|gif|bmp|tif|tiff|heic|heif|svg|ico|pdf)$/i.test(raw.name || "");

      if (!supported) { rejected.push(raw.name); continue; }

      try {
        if (isPdfLike(raw)) {
          const pages = await convertPdfToImages(raw, clamp(pdfRenderScale, 1, 4));
          pages.forEach(p => next.push({ id: idRef.current++, ...p }));
          continue;
        }

        const file = await normalizeInputFile(raw);
        if (!file.type.startsWith("image/")) { rejected.push(raw.name); continue; }

        let preview = "", unreadable = false, errMsg = "";
        try {
          preview = await readFileAsDataURL(file);
          await loadImageFromDataURL(preview);
        } catch {
          unreadable = true;
          errMsg = "Preview not supported in this browser.";
          preview = "";
        }

        next.push({
          id: idRef.current++, file,
          name: file.name, type: file.type, size: file.size,
          preview, unreadable, err: errMsg,
          originalName: raw.name, originalType: raw.type,
        });
      } catch (e) {
        console.error("add file error", e);
        rejected.push(raw.name);
      }
    }

    if (!next.length) {
      err("الرجاء اختيار صور أو PDF فقط. (PNG / JPG / WEBP / HEIC / AVIF / GIF / BMP / TIFF / SVG / ICO / PDF)");
      return;
    }

    setFiles(prev => [...prev, ...next]);
    if (rejected.length) {
      info(`تم تجاهل ${rejected.length} ملف(ات) غير مدعومة: ${rejected.slice(0, 4).join(", ")}${rejected.length > 4 ? "…" : ""}`);
    }
  }, [pdfRenderScale]);

  const onPick = e => addFiles(e.target.files);

  const onDrop = async e => {
    e.preventDefault(); e.stopPropagation();
    setDragOver(false);
    if (e.dataTransfer?.files?.length) await addFiles(e.dataTransfer.files);
  };

  const removeOne = id  => setFiles(p => p.filter(x => x.id !== id));
  const clearAll  = ()  => setFiles([]);

  const move = (id, dir) => {
    setFiles(prev => {
      const idx = prev.findIndex(x => x.id === id);
      if (idx < 0) return prev;
      const j = idx + dir;
      if (j < 0 || j >= prev.length) return prev;
      const copy = [...prev];
      [copy[idx], copy[j]] = [copy[j], copy[idx]];
      return copy;
    });
  };

  /* ── PDF export ── */
  const exportPdf = async () => {
    if (!files.length) return err("أضف صور أولاً.");
    const usable = files.filter(f => !f.unreadable);
    if (!usable.length) return err("لا توجد صور قابلة للمعالجة. جرّب PNG/JPG/WEBP.");
    setBusy(true);
    try {
      const doc  = new jsPDF({ orientation: pdfOrientation, unit: "mm", format: pdfPage });
      const pW   = doc.internal.pageSize.getWidth();
      const pH   = doc.internal.pageSize.getHeight();
      const m    = clamp(pdfMargin, 0, 40);
      const aW   = pW - m * 2;
      const aH   = pH - m * 2;

      for (let i = 0; i < usable.length; i++) {
        const res = await convertImageFile(usable[i].file, {
          outMime: "image/jpeg", quality: 0.92, maxW: 2400, maxH: 2400, keepAspect: true, jpgBg,
        });
        const dUrl = await readFileAsDataURL(new File([res.blob], "x.jpg", { type: "image/jpeg" }));
        const img  = await loadImageFromDataURL(dUrl);
        const iW   = img.naturalWidth || img.width;
        const iH   = img.naturalHeight || img.height;
        const r    = pdfFit === "cover" ? Math.max(aW / iW, aH / iH) : Math.min(aW / iW, aH / iH);
        const dW   = iW * r;
        const dH   = iH * r;
        if (i > 0) doc.addPage();
        doc.addImage(dUrl, "JPEG", m + (aW - dW) / 2, m + (aH - dH) / 2, dW, dH);
      }

      doc.save(`images_${Date.now()}.pdf`);
      ok("تم إنشاء PDF وتحميله بنجاح.");
    } catch (e) {
      console.error(e); err("فشل إنشاء PDF.");
    } finally {
      setBusy(false);
    }
  };

  /* ── Converter ── */
  const downloadConverted = async () => {
    if (!files.length) return err("أضف صور أولاً.");
    if (outMime === "application/pdf") { await exportPdf(); return; }
    const usable = files.filter(f => !f.unreadable);
    if (!usable.length) return err("لا توجد صور قابلة للتحويل في هذا المتصفح.");
    setBusy(true);
    try {
      const ext = extFromMime(outMime);

      if (outputMode === "single" && usable.length === 1) {
        const res = await convertImageFile(usable[0].file, {
          outMime, quality,
          maxW: maxW ? Number(maxW) : null,
          maxH: maxH ? Number(maxH) : null,
          keepAspect, jpgBg,
        });
        saveAs(res.blob, `${makeSafeName(singleName || "converted")}.${ext}`);
        ok("تم التحويل وتحميل الملف بنجاح.");
        return;
      }

      if (outputMode === "single" && usable.length !== 1) {
        info("Single mode يحتاج ملف واحد — سيتم التحميل كـ ZIP.");
      }

      const zip = new JSZip();
      for (const it of usable) {
        const res = await convertImageFile(it.file, {
          outMime, quality,
          maxW: maxW ? Number(maxW) : null,
          maxH: maxH ? Number(maxH) : null,
          keepAspect, jpgBg,
        });
        zip.file(`${makeSafeName(it.name || it.originalName || "img")}.${ext}`, res.blob);
      }

      saveAs(await zip.generateAsync({ type: "blob" }), `converted_${Date.now()}.zip`);
      ok(`تم تحويل ${usable.length} صورة وضغطها في ملف ZIP.`);
    } catch (e) {
      console.error(e); err("فشل التحويل. حاول مجدداً.");
    } finally {
      setBusy(false);
    }
  };

  /* ── Background Removal ── */
  const onBgPick = async file => {
    if (!file) return;
    setBgFile(file);
    setBgResultBlob(null);
    if (bgResultUrl) { URL.revokeObjectURL(bgResultUrl); setBgResultUrl(""); }
    setBgView("before");
    try   { setBgPreview(await readFileAsDataURL(file)); }
    catch { setBgPreview(""); }
  };

  const removeBgFn = async () => {
    if (!bgFile || bgBusy) return;
    setBgBusy(true);
    setBgPct(0);
    setBgPhase("جاري التهيئة…");
    setBgResultBlob(null);
    if (bgResultUrl) { URL.revokeObjectURL(bgResultUrl); setBgResultUrl(""); }

    try {
      const { removeBackground } = await import("@imgly/background-removal");
      const result = await removeBackground(bgFile, {
        publicPath: BG_CDN,
        model: bgModel,
        progress: (key, current, total) => {
          const pct = total > 0 ? Math.round((current / total) * 100) : 0;
          setBgPct(pct);
          setBgPhase(
            key.includes("wasm") || key.includes("ort")
              ? `جاري تحميل النموذج… ${pct}%`
              : "جاري معالجة الصورة…"
          );
        },
      });

      const url = URL.createObjectURL(result);
      setBgResultBlob(result);
      setBgResultUrl(url);
      setBgView("after");
      ok("تم تفريغ الصورة بنجاح.");
    } catch (e) {
      console.error(e);
      err("فشل تفريغ الصورة. تأكد من الاتصال بالإنترنت وحاول مرة أخرى.");
    } finally {
      setBgBusy(false);
      setBgPhase("");
    }
  };

  const downloadBgResult = async () => {
    if (!bgResultBlob || !bgFile) return;
    const base = makeSafeName(bgFile.name);

    if (bgOutputMode === "transparent") {
      saveAs(bgResultBlob, `${base}_nobg.png`);
      return;
    }
    // Composite result onto solid color
    const img    = await loadImageFromDataURL(bgResultUrl);
    const canvas = document.createElement("canvas");
    canvas.width  = img.naturalWidth  || img.width;
    canvas.height = img.naturalHeight || img.height;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = bgFill;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0);
    const blob = await new Promise(res => canvas.toBlob(res, "image/png", 1));
    saveAs(blob, `${base}_bg.png`);
  };

  /* ══════════════════════════════════════════
     RENDER
  ══════════════════════════════════════════ */
  return (
    <div className={styles.page}>

      {/* ── Header ── */}
      <div className={styles.header}>
        <div className={styles.headerIcon}><FiImage size={22} /></div>
        <div className={styles.headerText}>
          <h1 className={styles.pageTitle}>Image Tools</h1>
          <p className={styles.pageSubtitle}>Convert formats, resize, and create PDFs — all in the browser.</p>
        </div>
        <div className={styles.tabs}>
          {[
            { id: "convert", label: "Converter",   icon: <FiZap size={14}       /> },
            { id: "pdf",     label: "PDF Maker",   icon: <FiFileText size={14}  /> },
            { id: "bg",      label: "Remove BG",   icon: <FiScissors size={14}  /> },
          ].map(t => (
            <button key={t.id} type="button"
              className={`${styles.tab} ${tab === t.id ? styles.tabActive : ""}`}
              onClick={() => setTab(t.id)}>
              {t.icon} {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Alert ── */}
      {msg.text && (
        <div className={`${styles.alert}
          ${msg.kind === "err"  ? styles.alertErr  :
            msg.kind === "ok"   ? styles.alertOk   : styles.alertInfo}`}>
          {msg.kind === "err"  ? <FiAlertCircle size={15} /> :
           msg.kind === "ok"   ? <FiCheckCircle size={15} /> :
           <FiAlertCircle size={15} />}
          <span>{msg.text}</span>
          <button className={styles.alertClose} onClick={() => setMsg({ text:"", kind:"" })}>
            <FiX size={13} />
          </button>
        </div>
      )}

      {tab === "bg" && (
        <div className={styles.bgGrid}>

          {/* ══ BG LEFT: Image Preview ══ */}
          <div className={styles.card}>
            <div className={styles.secHeader}>
              <span className={styles.secDot} style={{ background: "#4a90e2" }} />
              <span className={styles.secLabel}>الصورة</span>
            </div>

            {!bgFile ? (
              <label className={styles.bgUpload}>
                <FiImage size={34} className={styles.bgUploadIcon} />
                <span className={styles.bgUploadTitle}>ارفع صورة لتفريغها</span>
                <span className={styles.bgUploadHint}>PNG · JPG · WEBP · وغيرها</span>
                <input type="file" accept="image/*" style={{ display: "none" }}
                  onChange={e => onBgPick(e.target.files?.[0])} />
              </label>
            ) : (
              <div>
                {/* Before / After toggle */}
                {bgResultUrl && (
                  <div className={styles.bgToggleRow}>
                    {["before", "after"].map(v => (
                      <button key={v} type="button"
                        className={`${styles.bgToggleBtn} ${bgView === v ? styles.bgToggleBtnActive : ""}`}
                        onClick={() => setBgView(v)}>
                        {v === "before" ? "الأصل" : "النتيجة"}
                      </button>
                    ))}
                  </div>
                )}

                {/* Image display */}
                <div className={`${styles.bgImgWrap} ${bgView === "after" ? styles.bgImgWrapChecker : ""}`}>
                  <img
                    src={bgView === "after" && bgResultUrl ? bgResultUrl : bgPreview}
                    alt={bgView}
                    className={styles.bgImg}
                  />
                </div>

                {/* Change image */}
                <div className={styles.bgChangeRow}>
                  <span className={styles.bgFileName}>{bgFile.name}</span>
                  <label className={styles.bgChangeBtn}>
                    تغيير الصورة
                    <input type="file" accept="image/*" style={{ display: "none" }}
                      onChange={e => onBgPick(e.target.files?.[0])} />
                  </label>
                </div>
              </div>
            )}
          </div>

          {/* ══ BG RIGHT: Settings ══ */}
          <div className={styles.bgSettingsCol}>

            {/* Model */}
            <div className={styles.card}>
              <div className={styles.secHeader}>
                <span className={styles.secDot} style={{ background: "#8b5cf6" }} />
                <span className={styles.secLabel}>جودة النموذج</span>
              </div>
              <div className={styles.modeRow}>
                {[
                  { id: "small",  label: "سريع",    hint: "أسرع — مناسب للاستخدام العادي" },
                  { id: "medium", label: "دقيق",    hint: "أبطأ — نتيجة أفضل للشعر والتفاصيل" },
                ].map(m => (
                  <button key={m.id} type="button"
                    className={`${styles.modeBtn} ${bgModel === m.id ? styles.modeBtnActive : ""}`}
                    onClick={() => setBgModel(m.id)}>
                    <span className={styles.modeLabel}>{m.label}</span>
                    <span className={styles.modeHint}>{m.hint}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Output */}
            <div className={styles.card}>
              <div className={styles.secHeader}>
                <span className={styles.secDot} style={{ background: "#10b981" }} />
                <span className={styles.secLabel}>نوع الإخراج</span>
              </div>
              <div className={styles.modeRow}>
                {[
                  { id: "transparent", label: "شفاف",       hint: "PNG بخلفية شفافة" },
                  { id: "color",       label: "لون مخصص",   hint: "ضع لوناً بدل الخلفية" },
                ].map(m => (
                  <button key={m.id} type="button"
                    className={`${styles.modeBtn} ${bgOutputMode === m.id ? styles.modeBtnActive : ""}`}
                    onClick={() => setBgOutputMode(m.id)}>
                    <span className={styles.modeLabel}>{m.label}</span>
                    <span className={styles.modeHint}>{m.hint}</span>
                  </button>
                ))}
              </div>

              {bgOutputMode === "color" && (
                <div className={styles.field} style={{ marginTop: 12 }}>
                  <label>لون الخلفية</label>
                  <div className={styles.colorPicker}>
                    <input type="color" value={bgFill} onChange={e => setBgFill(e.target.value)} />
                    <input type="text"  value={bgFill} onChange={e => setBgFill(e.target.value)} placeholder="#ffffff" />
                  </div>
                </div>
              )}
            </div>

            {/* Process button + progress */}
            <button className={styles.btnConvert} onClick={removeBgFn}
              disabled={bgBusy || !bgFile}>
              <FiScissors size={16} />
              {bgBusy ? bgPhase || "جاري المعالجة…" : "تفريغ الصورة"}
            </button>

            {bgBusy && (
              <div className={styles.bgProgressWrap}>
                <div className={styles.bgProgressBar}>
                  <div className={styles.bgProgressFill} style={{ width: `${bgPct}%` }} />
                </div>
                <span className={styles.bgProgressLabel}>{bgPct}%</span>
              </div>
            )}

            {/* Download */}
            {bgResultUrl && !bgBusy && (
              <button className={styles.btnDownloadBg} onClick={downloadBgResult}>
                <FiDownload size={15} />
                تحميل الصورة المُفرَّغة
              </button>
            )}

            <p className={styles.pdfNote}>
              المعالجة تتم بالكامل في المتصفح. قد يحتاج النموذج إلى تحميل (~40 MB) في أول استخدام.
            </p>
          </div>

        </div>
      )}

      {tab !== "bg" && <div className={styles.grid}>

        {/* ══ LEFT: File Manager ══ */}
        <div className={styles.card}>

          {/* Header */}
          <div className={styles.cardHead}>
            <div className={styles.cardHeadLeft}>
              <span className={styles.cardTitle}>Your Images</span>
              {files.length > 0 && (
                <span className={styles.countChip}>
                  {files.length} file{files.length !== 1 ? "s" : ""} • {totalSizeMb} MB
                </span>
              )}
            </div>
            <div className={styles.cardHeadRight}>
              <label className={styles.btnAdd}>
                <FiFilePlus size={14} />
                Add Files
                <input type="file" multiple
                  accept="application/pdf,image/*,.pdf,.png,.jpg,.jpeg,.webp,.heic,.heif,.avif,.gif,.bmp,.tif,.tiff,.svg,.ico"
                  onChange={onPick} style={{ display: "none" }} />
              </label>
              {files.length > 0 && (
                <button className={styles.btnClear} onClick={clearAll} disabled={busy}>
                  <FiTrash2 size={13} /> Clear
                </button>
              )}
            </div>
          </div>

          {/* Drop zone */}
          <div
            className={`${styles.dropZone} ${dragOver ? styles.dropZoneActive : ""}`}
            onDragEnter={e => { e.preventDefault(); e.stopPropagation(); setDragOver(true);  }}
            onDragOver ={e => { e.preventDefault(); e.stopPropagation(); setDragOver(true);  }}
            onDragLeave={e => { e.preventDefault(); e.stopPropagation(); setDragOver(false); }}
            onDrop={onDrop}
          >
            {files.length === 0 ? (
              <div className={styles.emptyState}>
                <div className={styles.emptyIcon}><FiImage size={36} /></div>
                <p className={styles.emptyTitle}>Drop images or PDF files here</p>
                <p className={styles.emptyHint}>
                  PNG · JPG · WEBP · HEIC · AVIF · GIF · BMP · TIFF · SVG · ICO · PDF
                </p>
                <p className={styles.emptyNote}>
                  HEIC (iPhone) → converted to PNG automatically.
                  PDF → each page becomes a separate image.
                </p>
                <label className={styles.emptyPickBtn}>
                  <FiFilePlus size={14} /> Choose Files
                  <input type="file" multiple
                    accept="application/pdf,image/*,.pdf,.png,.jpg,.jpeg,.webp,.heic,.heif,.avif,.gif,.bmp,.tif,.tiff,.svg,.ico"
                    onChange={onPick} style={{ display: "none" }} />
                </label>
              </div>
            ) : (
              <div className={styles.fileList}>
                {files.map((it, idx) => {
                  const ext = extFromMime(it.originalType || it.type);
                  return (
                    <div key={it.id} className={`${styles.fileItem} ${it.unreadable ? styles.fileItemWarn : ""}`}>
                      {/* Thumbnail */}
                      <div className={styles.fileThumbnail}>
                        {it.preview
                          ? <img src={it.preview} alt={it.name} className={styles.fileThumb} />
                          : <div className={styles.fileThumbPlaceholder}>{ext.toUpperCase()}</div>
                        }
                      </div>

                      {/* Meta */}
                      <div className={styles.fileMeta}>
                        <div className={styles.fileName} title={it.name}>{it.name}</div>
                        <div className={styles.fileTags}>
                          <span className={styles.fileExt}>{ext.toUpperCase()}</span>
                          <span className={styles.fileSize}>{(it.size / 1024).toFixed(1)} KB</span>
                          {it.fromPdf && (
                            <span className={styles.pdfTag}>PDF · page {it.pdfPageNumber}</span>
                          )}
                          {it.unreadable && (
                            <span className={styles.warnTag}>⚠ Preview N/A</span>
                          )}
                        </div>
                      </div>

                      {/* Order + remove */}
                      <div className={styles.fileActions}>
                        <button className={styles.orderBtn} onClick={() => move(it.id, -1)} disabled={idx === 0} title="Move up">
                          <FiArrowUp size={13} />
                        </button>
                        <button className={styles.orderBtn} onClick={() => move(it.id, +1)} disabled={idx === files.length - 1} title="Move down">
                          <FiArrowDown size={13} />
                        </button>
                        <button className={styles.removeBtn} onClick={() => removeOne(it.id)} title="Remove">
                          <FiX size={13} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* ══ RIGHT: Settings ══ */}
        <div className={styles.settingsCol}>

          {tab === "convert" ? (
            <>
              {/* Section: Output */}
              <div className={styles.card}>
                <div className={styles.secHeader}>
                  <span className={styles.secDot} style={{ background: "#4a90e2" }} />
                  <span className={styles.secLabel}>Output Format</span>
                </div>

                <div className={styles.formatGrid}>
                  {[
                    { mime: "image/png",      label: "PNG",  hint: "Lossless + transparency" },
                    { mime: "image/jpeg",     label: "JPG",  hint: "Smaller file size"        },
                    { mime: "image/webp",     label: "WEBP", hint: "Modern + compact"          },
                    { mime: "application/pdf",label: "PDF",  hint: "One image per page"        },
                  ].map(f => (
                    <button key={f.mime} type="button"
                      className={`${styles.formatBtn} ${outMime === f.mime ? styles.formatBtnActive : ""}`}
                      onClick={() => setOutMime(f.mime)}>
                      <span className={styles.formatLabel}>{f.label}</span>
                      <span className={styles.formatHint}>{f.hint}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Section: Quality & Color */}
              <div className={styles.card}>
                <div className={styles.secHeader}>
                  <span className={styles.secDot} style={{ background: "#f5a623" }} />
                  <span className={styles.secLabel}>Quality & Color</span>
                </div>

                <div className={styles.field}>
                  <label>
                    Quality (JPG / WEBP)
                    <strong className={styles.liveVal}>
                      {outMime === "image/png" || outMime === "application/pdf"
                        ? " — N/A"
                        : ` — ${Math.round(quality * 100)}%`}
                    </strong>
                  </label>
                  <input type="range" min="0.5" max="1" step="0.01" value={quality}
                    onChange={e => setQuality(Number(e.target.value))}
                    disabled={outMime === "image/png" || outMime === "application/pdf"}
                    className={styles.slider} />
                  <div className={styles.sliderTicks}><span>50%</span><span>100%</span></div>
                </div>

                {outMime === "image/jpeg" && (
                  <div className={styles.field}>
                    <label>JPG Background Color <small>(for transparent images)</small></label>
                    <div className={styles.colorPicker}>
                      <input type="color" value={jpgBg} onChange={e => setJpgBg(e.target.value)} />
                      <input type="text"  value={jpgBg} onChange={e => setJpgBg(e.target.value)} placeholder="#ffffff" />
                    </div>
                  </div>
                )}
              </div>

              {/* Section: Resize */}
              <div className={styles.card}>
                <div className={styles.secHeader}>
                  <span className={styles.secDot} style={{ background: "#8b5cf6" }} />
                  <span className={styles.secLabel}>Resize <span className={styles.secOptional}>(optional)</span></span>
                </div>

                <div className={styles.dimRow}>
                  <div className={styles.field}>
                    <label>Max Width</label>
                    <div className={styles.dimInput}>
                      <input value={maxW} onChange={e => setMaxW(e.target.value)} placeholder="e.g. 1200" />
                      <span>px</span>
                    </div>
                  </div>
                  <div className={styles.field}>
                    <label>Max Height</label>
                    <div className={styles.dimInput}>
                      <input value={maxH} onChange={e => setMaxH(e.target.value)} placeholder="e.g. 1200" />
                      <span>px</span>
                    </div>
                  </div>
                </div>

                <label className={styles.checkRow}>
                  <input type="checkbox" checked={keepAspect} onChange={e => setKeepAspect(e.target.checked)} />
                  <span>Keep aspect ratio</span>
                </label>
              </div>

              {/* Section: Output Mode */}
              {outMime !== "application/pdf" && (
                <div className={styles.card}>
                  <div className={styles.secHeader}>
                    <span className={styles.secDot} style={{ background: "#10b981" }} />
                    <span className={styles.secLabel}>Output Mode</span>
                  </div>

                  <div className={styles.modeRow}>
                    {[
                      { id: "zip",    label: "ZIP",    hint: "All files in one archive" },
                      { id: "single", label: "Single", hint: "One file (1 image only)"  },
                    ].map(m => (
                      <button key={m.id} type="button"
                        className={`${styles.modeBtn} ${outputMode === m.id ? styles.modeBtnActive : ""}`}
                        onClick={() => setOutputMode(m.id)}>
                        <span className={styles.modeLabel}>{m.label}</span>
                        <span className={styles.modeHint}>{m.hint}</span>
                      </button>
                    ))}
                  </div>

                  {outputMode === "single" && (
                    <div className={styles.field} style={{ marginTop: 10 }}>
                      <label>File Name</label>
                      <input value={singleName} onChange={e => setSingleName(e.target.value)} placeholder="converted" />
                    </div>
                  )}
                </div>
              )}

              {/* Section: PDF Input Quality */}
              <div className={styles.card}>
                <div className={styles.secHeader}>
                  <span className={styles.secDot} style={{ background: "#64748b" }} />
                  <span className={styles.secLabel}>PDF Input Quality</span>
                </div>
                <div className={styles.field}>
                  <label>Render Scale <small>(when uploading a PDF)</small></label>
                  <select value={pdfRenderScale} onChange={e => setPdfRenderScale(Number(e.target.value))}>
                    <option value={1}>Low (1×)</option>
                    <option value={1.5}>Medium (1.5×)</option>
                    <option value={2}>High (2×)</option>
                    <option value={3}>Very High (3×)</option>
                  </select>
                </div>
              </div>

              <button className={styles.btnConvert} onClick={downloadConverted} disabled={busy || !files.length}>
                <FiDownload size={16} />
                {busy ? "Working…" : outMime === "application/pdf" ? "Create PDF" : "Convert & Download"}
              </button>
            </>
          ) : (
            <>
              {/* PDF Maker */}
              <div className={styles.card}>
                <div className={styles.secHeader}>
                  <span className={styles.secDot} style={{ background: "#4a90e2" }} />
                  <span className={styles.secLabel}>Page Layout</span>
                </div>

                <div className={styles.dimRow}>
                  <div className={styles.field}>
                    <label>Page Size</label>
                    <select value={pdfPage} onChange={e => setPdfPage(e.target.value)}>
                      <option value="a4">A4</option>
                      <option value="letter">Letter</option>
                    </select>
                  </div>
                  <div className={styles.field}>
                    <label>Orientation</label>
                    <select value={pdfOrientation} onChange={e => setPdfOrientation(e.target.value)}>
                      <option value="p">Portrait</option>
                      <option value="l">Landscape</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className={styles.card}>
                <div className={styles.secHeader}>
                  <span className={styles.secDot} style={{ background: "#f5a623" }} />
                  <span className={styles.secLabel}>Image Settings</span>
                </div>

                <div className={styles.field}>
                  <label>
                    Margin — <strong>{pdfMargin} mm</strong>
                  </label>
                  <input type="range" min="0" max="40" step="1" value={pdfMargin}
                    onChange={e => setPdfMargin(Number(e.target.value))}
                    className={styles.slider} />
                  <div className={styles.sliderTicks}><span>0 mm</span><span>40 mm</span></div>
                </div>

                <div className={styles.field} style={{ marginTop: 14 }}>
                  <label>Image Fit</label>
                  <div className={styles.modeRow}>
                    {[
                      { id: "contain", label: "Contain", hint: "No crop, letterboxed" },
                      { id: "cover",   label: "Cover",   hint: "Fill page, may crop"  },
                    ].map(m => (
                      <button key={m.id} type="button"
                        className={`${styles.modeBtn} ${pdfFit === m.id ? styles.modeBtnActive : ""}`}
                        onClick={() => setPdfFit(m.id)}>
                        <span className={styles.modeLabel}>{m.label}</span>
                        <span className={styles.modeHint}>{m.hint}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className={styles.field} style={{ marginTop: 14 }}>
                  <label>Background Color <small>(for transparent images)</small></label>
                  <div className={styles.colorPicker}>
                    <input type="color" value={jpgBg} onChange={e => setJpgBg(e.target.value)} />
                    <input type="text"  value={jpgBg} onChange={e => setJpgBg(e.target.value)} placeholder="#ffffff" />
                  </div>
                </div>
              </div>

              <button className={styles.btnConvert} onClick={exportPdf} disabled={busy || !files.length}>
                <FiFile size={16} />
                {busy ? "Creating PDF…" : "Create & Download PDF"}
              </button>

              <p className={styles.pdfNote}>
                كل صورة تُضاف كصفحة منفصلة في الـ PDF بالترتيب الظاهر في القائمة.
              </p>
            </>
          )}
        </div>
      </div>}

    </div>
  );
}
